import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface WhatsAppSettings {
  id: string;
  phone_number: string;
  display_name: string;
  welcome_message: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const useWhatsAppSettings = () => {
  return useQuery({
    queryKey: ["whatsapp-settings"],
    queryFn: async () => {
      // First check if WhatsApp is enabled in store settings
      const { data: storeSettings } = await supabase
        .from("store_settings")
        .select("value")
        .eq("key", "whatsapp_enabled")
        .maybeSingle();

      // If explicitly disabled in store settings, return null
      if (storeSettings?.value === false) {
        return null;
      }

      // Get WhatsApp settings from whatsapp_settings table
      const { data, error } = await supabase
        .from("whatsapp_settings")
        .select("*")
        .eq("is_active", true)
        .maybeSingle();

      if (error) throw error;
      return data as WhatsAppSettings | null;
    },
  });
};

export const useUpdateWhatsAppSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...settings }: Partial<WhatsAppSettings> & { id: string }) => {
      const { data, error } = await supabase
        .from("whatsapp_settings")
        .update(settings)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["whatsapp-settings"] });
      toast.success("WhatsApp settings updated");
    },
    onError: (error) => {
      toast.error("Failed to update settings: " + error.message);
    },
  });
};

export const generateWhatsAppLink = (
  phoneNumber: string,
  message: string
): string => {
  const cleanNumber = phoneNumber.replace(/[^0-9]/g, "");
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${cleanNumber}?text=${encodedMessage}`;
};

export const generateOrderWhatsAppMessage = (orderNumber: string): string => {
  return `Hi! I have a question about my order ${orderNumber}. Can you help me?`;
};

export const generateProductWhatsAppMessage = (productName: string): string => {
  return `Hi! I'm interested in "${productName}". Can you tell me more about it?`;
};

export const generateGeneralWhatsAppMessage = (): string => {
  return `Namaste! 🙏 I would like to know more about your handcrafted products.`;
};
